import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Monitor, Smartphone, Tablet } from "lucide-react";
import { format } from "date-fns";

interface DrillDownDay {
  date: string;
  sessionsCount: number;
  sessionsRolled: number;
  activeMs: number;
  firstLogin: string | null;
  lastActivity: string | null;
}

interface DrillDownLogin {
  id: string;
  startedAt: string | null;
  endedAt: string | null;
  durationMs: number;
  isOnline: boolean;
  browser: string;
  deviceType: string;
  ipAddress: string | null;
}

interface DrillDownResponse {
  userId: string;
  userName: string;
  email: string | null;
  period: { start: string; end: string };
  days: DrillDownDay[];
  loginHistory: DrillDownLogin[];
}

function formatActiveMs(ms: number): string {
  if (!ms || ms < 1000) return "0m";
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function DeviceIcon({ deviceType }: { deviceType: string }) {
  if (deviceType === "Mobile") return <Smartphone className="w-3 h-3" />;
  if (deviceType === "Tablet") return <Tablet className="w-3 h-3" />;
  return <Monitor className="w-3 h-3" />;
}

interface Props {
  open: boolean;
  userId: string | null;
  userName: string | null;
  start: Date;
  end: Date;
  onOpenChange: (open: boolean) => void;
}

export function ActivityUserDrillDown({ open, userId, userName, start, end, onOpenChange }: Props) {
  const detailQuery = useQuery<DrillDownResponse>({
    queryKey: ["/api/admin/activity-tracker/user-detail", userId, start.toISOString(), end.toISOString()],
    queryFn: async () => {
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString(),
      });
      const response = await fetch(`/api/admin/activity-tracker/user-detail/${userId}?${params}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch user detail");
      return response.json();
    },
    enabled: !!userId && open,
  });

  const data = detailQuery.data;
  const maxActiveMs = data ? Math.max(1, ...data.days.map(d => d.activeMs)) : 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dialog-user-drilldown">
        <DialogHeader>
          <DialogTitle data-testid="text-drilldown-username">
            {userName || data?.userName || "User Activity"}
          </DialogTitle>
        </DialogHeader>

        {detailQuery.isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : !data ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="text-drilldown-empty">
            No data available for this user.
          </div>
        ) : (
          <div className="space-y-6">
            {/* Per-day bar chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Active Minutes by Day</CardTitle>
              </CardHeader>
              <CardContent>
                {data.days.length === 0 || data.days.every(d => d.activeMs === 0) ? (
                  <p className="text-sm text-muted-foreground py-6 text-center" data-testid="text-no-active-days">
                    No active time recorded for this range.
                  </p>
                ) : (
                  <div className="flex items-end gap-2 h-32" data-testid="chart-active-by-day">
                    {data.days.map(d => {
                      const minutes = Math.round(d.activeMs / 60000);
                      const heightPct = (d.activeMs / maxActiveMs) * 100;
                      return (
                        <div key={d.date} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                          <div className="text-xs text-muted-foreground">{minutes || ""}</div>
                          <div
                            className="w-full bg-primary/80 rounded-t hover-elevate"
                            style={{ height: `${Math.max(2, heightPct)}%`, minHeight: d.activeMs > 0 ? 4 : 2, opacity: d.activeMs > 0 ? 1 : 0.2 }}
                            title={`${d.date}: ${formatActiveMs(d.activeMs)}`}
                            data-testid={`bar-active-${d.date}`}
                          />
                          <div className="text-[10px] text-muted-foreground">
                            {format(new Date(d.date + "T00:00:00"), "MMM d")}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Per-day table */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Daily Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Sessions</TableHead>
                        <TableHead title="Idle rolls — number of times the user stepped away for more than 5 minutes and then returned, starting a new session block.">Rolls</TableHead>
                        <TableHead title="Time actively engaged (clicking, typing, scrolling). Sums the gaps between interactions; idle gaps longer than 5 minutes are excluded. Continuous work is never capped.">Active Time</TableHead>
                        <TableHead>First Login</TableHead>
                        <TableHead>Last Activity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.days.map(d => (
                        <TableRow key={d.date} data-testid={`row-drilldown-day-${d.date}`}>
                          <TableCell>{format(new Date(d.date + "T00:00:00"), "EEE MMM d")}</TableCell>
                          <TableCell data-testid={`text-drilldown-sessions-${d.date}`}>{d.sessionsCount}</TableCell>
                          <TableCell data-testid={`text-drilldown-rolls-${d.date}`} className="text-muted-foreground">
                            {d.sessionsRolled || 0}
                          </TableCell>
                          <TableCell data-testid={`text-drilldown-active-${d.date}`}>
                            {formatActiveMs(d.activeMs)}
                          </TableCell>
                          <TableCell>
                            {d.firstLogin ? format(new Date(d.firstLogin), "h:mm a") : "—"}
                          </TableCell>
                          <TableCell>
                            {d.lastActivity ? format(new Date(d.lastActivity), "h:mm a") : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                      {data.days.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                            No days in range.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Login history */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Recent Login History</CardTitle>
              </CardHeader>
              <CardContent>
                {data.loginHistory.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center" data-testid="text-no-login-history">
                    No recent sessions.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Start</TableHead>
                          <TableHead>End</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Device / Browser</TableHead>
                          <TableHead>IP</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.loginHistory.map(s => (
                          <TableRow key={s.id} data-testid={`row-login-${s.id}`}>
                            <TableCell className="whitespace-nowrap">
                              {s.startedAt ? format(new Date(s.startedAt), "MMM d, h:mm a") : "—"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {s.isOnline ? (
                                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30" data-testid={`badge-online-${s.id}`}>
                                  Online now
                                </Badge>
                              ) : s.endedAt ? (
                                format(new Date(s.endedAt), "MMM d, h:mm a")
                              ) : "—"}
                            </TableCell>
                            <TableCell data-testid={`text-login-duration-${s.id}`}>
                              {formatActiveMs(s.durationMs)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2 text-sm">
                                <DeviceIcon deviceType={s.deviceType} />
                                <span>{s.deviceType} · {s.browser}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {s.ipAddress || "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
